import {
  CrmFieldService,
  StandardIds,
} from '@galaxy/crm/services/field-service';
import { Injectable } from '@angular/core';
import {
  CRMFilterOverride,
  TableCustomCell,
} from '../../tokens-and-interfaces';
import {
  CrmObjectInterface,
  FieldSchemaInterface,
  SortByInterface,
} from '@vendasta/crm';
import { Observable } from 'rxjs';
import {
  CellData,
  CustomCell,
  GalaxyColumnDef,
  Row,
} from '@vendasta/galaxy/table';
import {
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInterface,
} from '@vendasta/galaxy/filter/chips';

export type CustomRowStyle = (row: Row) => RowStyle;

export interface RowStyle {
  color?: string;
  'text-decoration'?: string;
}

export interface ColumnDataInterface {
  [columnId: string]: CellData;
}

export interface TableCustomizationServiceInterface {
  defaultSortingOptions?: SortByInterface[];

  customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[];

  customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void | Promise<void>;

  getRowFullName(crmObject: CrmObjectInterface): string;

  customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]>;

  getAssociationInfo(crmObject: CrmObjectInterface): string;

  getInitialFilters(
    filterDefinitions$: Observable<GalaxyFilterDefinitionInterface[]>,
  ): Promise<GalaxyFilterInterface[]>;
}

@Injectable()
// TODO: transform/split part of this into overall customization service if
// ends up having to be used everywhere
export abstract class TableCustomizationService implements TableCustomizationServiceInterface {
  constructor(protected readonly crmFieldService: CrmFieldService) {}

  private addCustomCell(
    tableCustomCell: TableCustomCell,
    crmObject: CrmObjectInterface,
    columnData: ColumnDataInterface,
  ): void {
    const cellValues = tableCustomCell.fieldIds.map((fieldId) => {
      return this.crmFieldService.getFieldValueFromCrmObject(crmObject, fieldId);
    });

    if ('namespace' in crmObject) {
      const namespaceValue = (crmObject as { namespace: string }).namespace;
      cellValues.push({ fieldId: 'namespace', stringValue: namespaceValue });
    }

    if ('crmObjectId' in crmObject) {
      const crmObjectId = (crmObject as { crmObjectId: string }).crmObjectId;
      cellValues.push({ fieldId: 'crmObjectId', stringValue: crmObjectId });
    }
    columnData[tableCustomCell.columnDefinition.id] = {
      cellType: 'custom',
      value: cellValues,
      component: tableCustomCell.customCellComponent,
    } as CustomCell;
  }

  protected addCustomCells(
    tableCustomCells: TableCustomCell[],
    crmObject: CrmObjectInterface,
    columnData: ColumnDataInterface,
  ): void {
    for (const tableCustomCell of tableCustomCells) {
      this.addCustomCell(tableCustomCell, crmObject, columnData);
    }
  }

  protected addCustomColumns(customColumns: GalaxyColumnDef[], columns: GalaxyColumnDef[]): GalaxyColumnDef[] {
    const joinedColumns = [
      // default custom columns as hidden true
      ...columns,
      ...customColumns.map((customColumn) => ({ ...customColumn, hidden: true })),
    ];
    const dedupedColumns = joinedColumns.filter(
      (column, index, self) => self.findIndex((c) => c.id === column.id) === index,
    );

    return dedupedColumns;
  }

  protected overrideCustomFilters(
    filters: GalaxyFilterDefinitionInterface[],
    overrides: CRMFilterOverride[],
  ): GalaxyFilterDefinitionInterface[] {
    for (const override of overrides) {
      const findFieldIDIndex = filters.findIndex((f) => f.fieldId === override.fieldId);
      if (findFieldIDIndex >= 0 && filters[findFieldIDIndex]) {
        filters[findFieldIDIndex].fieldName = override.title;
      }
    }
    return filters;
  }

  abstract customizeColumns(columns: GalaxyColumnDef[], isMobile: boolean): GalaxyColumnDef[];

  abstract customizeRowData(
    crmObject: CrmObjectInterface,
    schemaMap: { [schemaId: string]: FieldSchemaInterface },
    columnData: ColumnDataInterface,
  ): void | Promise<void>;

  abstract getRowFullName(crmObject: CrmObjectInterface): string;

  abstract customizeFilters(filters: GalaxyFilterDefinitionInterface[]): Observable<GalaxyFilterDefinitionInterface[]>;

  abstract getAssociationInfo(crmObject: CrmObjectInterface): string;

  abstract getInitialFilters(
    filterDefinitions$: Observable<GalaxyFilterDefinitionInterface[]>,
  ): Promise<GalaxyFilterInterface[]>;
}

@Injectable()
export abstract class ActivityTableCustomizationService extends TableCustomizationService {
  getRowFullName(crmObject: CrmObjectInterface): string {
    const name = this.crmFieldService.getFieldId(StandardIds.ActivityName);
    const nameData = crmObject.fields?.find((f) => f.fieldId === name);
    return nameData?.stringValue || '';
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  getAssociationInfo(crmObject: CrmObjectInterface): string {
    return '';
  }

  abstract override getInitialFilters(): Promise<GalaxyFilterInterface[]>;
}
